import { FilterList as FilterListIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface RelationshipEntityTypeFilterOption {
  id: string;
  label: string;
}

export interface RelationshipEntityTypeFilterProps {
  hiddenEntityTypeIds: ReadonlySet<string>;
  onShowAll: () => void;
  onToggle: (entityTypeId: string) => void;
  options: ReadonlyArray<RelationshipEntityTypeFilterOption>;
}

export function RelationshipEntityTypeFilter({
  hiddenEntityTypeIds,
  onShowAll,
  onToggle,
  options,
}: Readonly<RelationshipEntityTypeFilterProps>) {
  const { t } = useTranslation();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const isFiltering = hiddenEntityTypeIds.size > 0;
  const popoverId = anchorElement ? 'relationship-entity-type-filter' : undefined;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  return (
    <>
      <Tooltip title={t('relationships.filter_entity_types')}>
        <IconButton
          aria-controls={popoverId}
          aria-expanded={Boolean(anchorElement)}
          aria-haspopup="true"
          aria-label={t('relationships.filter_entity_types')}
          color={isFiltering ? 'primary' : 'default'}
          onClick={handleOpen}
          size="small"
          sx={{
            border: 1,
            borderColor: isFiltering ? 'primary.main' : 'divider',
          }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        id={popoverId}
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(anchorElement)}
        onClose={handleClose}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { minWidth: 260, maxWidth: 360 },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {t('relationships.entity_types')}
          </Typography>
          <Button
            disabled={!isFiltering}
            onClick={onShowAll}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('relationships.select_all_entity_types')}
          </Button>
        </Box>
        <Divider />
        <List dense disablePadding sx={{ py: 0.75 }}>
          {options.map((option, optionIndex) => {
            const labelId = `relationship-entity-type-filter-option-${optionIndex}`;
            const isSelected = !hiddenEntityTypeIds.has(option.id);

            return (
              <ListItemButton
                key={option.id}
                dense
                onClick={() => onToggle(option.id)}
                role={undefined}
              >
                <ListItemIcon sx={{ minWidth: 38 }}>
                  <Checkbox
                    checked={isSelected}
                    disableRipple
                    edge="start"
                    size="small"
                    slotProps={{ input: { 'aria-labelledby': labelId } }}
                    tabIndex={-1}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={option.label} />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </>
  );
}
